import { History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import type { ProductVersionEntry } from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';
import { formatUtcNaiveDateTime } from '~/utils/date';

interface Props {
  entries: ProductVersionEntry[];
  isLoading: boolean;
  /** The request failed. Distinct from an empty timeline: "no versions yet" is a
   *  claim about the product, and we cannot make it when the call did not
   *  answer. Same null-is-not-zero discipline the rest of this feature follows. */
  isError?: boolean;
  /** Version currently being viewed — the live one unless the reader picked an
   *  older entry. */
  selectedVersion: number | null;
  onSelect: (version: number | null) => void;
  /** Copy differs between the feed timeline and the curated product's own: they
   *  are separate entities and conflating them is the trap this feature carries. */
  variant: 'feed' | 'internal';
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}

/**
 * A product's version history, newest first, rendered as a change log.
 *
 * `changedFields` is computed server-side against the NEXT OLDER version, not
 * against the live row — so a three-entry timeline reads "v3 changed title",
 * "v2 changed price", and empty on the oldest, which has nothing behind it.
 * Never diff this client-side: bookkeeping columns and `content_hash` are
 * excluded upstream and would otherwise report every field as changed.
 */
export function ProductVersionTimeline({
  entries,
  isLoading,
  isError = false,
  selectedVersion,
  onSelect,
  variant,
  testId = `product-detail-version-timeline-${variant}`,
}: Props) {
  const { t } = useTranslation('catalog-operations');

  if (isLoading) {
    return (
      <div data-testid={`${testId}-loading`} className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  // Order matters: a failed request must never fall through to the empty state,
  // which would assert the product has no history when we simply don't know.
  if (isError) {
    return (
      <p data-testid={`${testId}-error`} className="text-muted-foreground text-sm">
        {t('versions.loadFailed')}
      </p>
    );
  }

  if (entries.length === 0) {
    return (
      <p data-testid={`${testId}-empty`} className="text-muted-foreground text-sm">
        {t(`versions.empty.${variant}`)}
      </p>
    );
  }

  return (
    <ol data-testid={testId} className="space-y-2">
      {entries.map((entry) => {
        // The live entry is the default view, so it is "selected" both when it
        // is explicitly chosen and when nothing is.
        const isSelected = entry.isCurrent
          ? selectedVersion === null
          : selectedVersion === entry.version;
        return (
          <li key={entry.version} data-testid={`${testId}-item-${String(entry.version)}`}>
            <button
              data-testid={`${testId}-item-${String(entry.version)}-button`}
              type="button"
              onClick={() => {
                onSelect(entry.isCurrent ? null : entry.version);
              }}
              className={cn(
                'w-full cursor-pointer rounded-lg border px-4 py-3 text-left transition-colors',
                isSelected ? 'border-primary bg-accent-100' : 'hover:bg-muted bg-white',
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-foreground text-sm font-semibold">
                  {t('versions.versionLabel', { version: entry.version })}
                </span>
                {entry.isCurrent && (
                  <span
                    data-testid={`${testId}-item-${String(entry.version)}-current-badge`}
                    className="bg-success-50 text-success rounded-full px-2 py-0.5 text-xs font-medium"
                  >
                    {t('versions.current')}
                  </span>
                )}
                {/* `validFrom` is null on rows that predate versioning — the
                    column was added without a default precisely so a backfill
                    couldn't claim every product went live on deploy day. Show
                    nothing rather than inventing a date. */}
                {entry.validFrom !== null && (
                  <span className="text-muted-foreground text-xs">
                    {formatUtcNaiveDateTime(entry.validFrom)}
                  </span>
                )}
              </div>

              <p
                data-testid={`${testId}-item-${String(entry.version)}-changes`}
                className="text-muted-foreground mt-1 text-sm"
              >
                {entry.changedFields.length > 0
                  ? t('versions.changedFields', {
                      fields: entry.changedFields.join(', '),
                    })
                  : t('versions.noComparison')}
              </p>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** Banner shown above the detail body while an older version is being viewed —
 *  without it the page looks like current data under a different heading. */
export function ViewingVersionBanner({
  version,
  onReturn,
}: {
  version: number;
  onReturn: () => void;
}) {
  const { t } = useTranslation('catalog-operations');
  return (
    <div
      data-testid="product-detail-viewing-version-banner"
      className="border-warning bg-warning-50 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-l-[3px] px-4 py-3"
    >
      <span className="text-foreground inline-flex items-center gap-2 text-sm">
        <History className="size-4 shrink-0" aria-hidden="true" />
        {t('versions.viewingHistorical', { version })}
      </span>
      <Button
        data-testid="product-detail-viewing-version-back-button"
        type="button"
        variant="outline"
        size="sm"
        className="cursor-pointer"
        onClick={onReturn}
      >
        {t('versions.backToCurrent')}
      </Button>
    </div>
  );
}
